import React from 'react';
import PropTypes from 'prop-types';
import JsonRpc from '@statianzo/pmrpc';
import {parseQuerystring} from 'utilities/URL';
export const RpcContext = React.createContext();

export const getReferenceId = query => {
  if (query.referenceId && query.referenceId.match(/^\w{1,100}$/)) {
    return query.referenceId;
  }
  return '';
};

export const cleanFiltersForRpc = filters => {
  const rcpFilters = [];
  filters.forEach(filter => {
    rcpFilters.push({
      column: filter.column,
      dataType: filter.dataType,
      label: filter.label,
      operand: filter.operand,
      values: filter.values,
    });
  });
  return rcpFilters;
};

const urlParams = new URLSearchParams(window.location.search);
const debug = urlParams.has('debug-js-api');

class RpcEndpointHook extends React.Component {
  componentDidMount() {
    this.props.bindMethod(this.props.method, this.props.onCall);
  }

  componentWillUnmount() {
    this.props.unbindMethod(this.props.method);
  }

  render() {
    return null;
  }
}

// defaulting for when RpcEndpointProvider is not an ancestor (davinci)
RpcEndpointHook.defaultProps = {
  bindMethod: () => {},
  unbindMethod: () => {},
};

const RpcEndpoint = ({method, onCall}) => (
  <RpcContext.Consumer>
    {rpcProps => (
      <RpcEndpointHook {...rpcProps} method={method} onCall={onCall} />
    )}
  </RpcContext.Consumer>
);

RpcEndpoint.propTypes = {
  method: PropTypes.string.isRequired,
  onCall: PropTypes.func.isRequired,
};

export default RpcEndpoint;

export class RpcEndpointProvider extends React.Component {
  isConnected = false;
  channel = new MessageChannel();
  rpc = new JsonRpc({
    source: this.channel.port1,
    destination: this.channel.port1,
  });
  referenceId = getReferenceId(parseQuerystring());

  componentDidMount() {
    this.channel.port1.start();
  }

  componentWillUnmount() {
    this.channel.port1.close();
  }

  connect = () => {
    if (this.isConnected) return;
    if (!window.ENV || !window.ENV.hasAuthorizedDomains) {
      if (debug)
        console.warn(
          'Domo JS API: Not initializing JS API because embed authorized domains are not set'
        ); // eslint-disable-line no-console
      return;
    }

    if (debug) console.log('Domo JS API: Initializing'); // eslint-disable-line no-console

    window.parent.postMessage(
      {
        upgrade: 'JSON-RPC 2.0',
        referenceId: this.referenceId,
      },
      '*',
      [this.channel.port2]
    );

    this.isConnected = true;
  };

  applyMethod = (method, rest) => {
    if (debug) console.log('Domo JS API: Sending message from', method, rest); // eslint-disable-line no-console
    this.rpc.apply(method, rest);
  };

  bindMethod = (method, fn) => {
    this.connect();
    this.rpc.methods[method] = (...args) => {
      if (debug)
        console.log('Domo JS API: receiving  message from', method, ...args); // eslint-disable-line no-console
      fn(...args);
    };
  };

  unbindMethod = method => delete this.rpc.methods[method];

  render() {
    return (
      <RpcContext.Provider
        value={{
          bindMethod: this.bindMethod,
          unbindMethod: this.unbindMethod,
          applyMethod: this.applyMethod,
        }}
      >
        {this.props.children}
      </RpcContext.Provider>
    );
  }
}
